import { useEffect, useMemo, useRef } from 'react';
import type { AnalysisResult } from '@/core/analysis/types';
import { pianoKeys, TUNINGS, type Instrument } from '@/core/instruments/shapes';
import type { DisplayOptions } from '@/core/theory/display';
import { prefersReducedMotion } from '@/lib/motion';
import { useTimeIndex } from '@/lib/useTimeIndex';
import { Segmented } from '@/ui/Segmented';
import { layoutFor, STRING_SPACING } from './chordShape';
import { InstrumentSelect } from './InstrumentSelect';
import styles from './DiagramStrip.module.css';
import { useVoicingPrefs } from './useVoicingPrefs';
import { FretDiagram } from './FretDiagram';
import { PianoDiagram } from './PianoDiagram';
import { currentItem, sequenceItems, summaryItems } from './stripModel';

export type StripMode = 'animated' | 'summary';

interface DiagramStripProps {
  result: AnalysisResult;
  options: DisplayOptions;
  instrument: Instrument;
  onInstrument: (instrument: Instrument) => void;
  leftHanded: boolean;
  mode: StripMode;
  onMode: (mode: StripMode) => void;
  visualOffsetMs: number;
  onSeekBeat: (beat: number) => void;
}

const MODES = [
  {
    value: 'animated' as const,
    label: 'Animated',
    ariaLabel: 'Animated: every chord change in order',
  },
  { value: 'summary' as const, label: 'Summary', ariaLabel: 'Summary: each chord once' },
];

const USER_SCROLL_PAUSE_MS = 3000;

/**
 * A row of chord diagrams: every chord change in song order, following the music (Animated),
 * or each chord once (Summary).
 */
export function DiagramStrip({
  result,
  options,
  instrument,
  onInstrument,
  leftHanded,
  mode,
  onMode,
  visualOffsetMs,
  onSeekBeat,
}: DiagramStripProps) {
  const items = useMemo(
    () => (mode === 'animated' ? sequenceItems : summaryItems)(result.segments, options),
    [mode, result, options],
  );
  const starts = useMemo(() => result.segments.map((s) => s.start), [result]);
  const segment = useTimeIndex(starts, visualOffsetMs);
  const current = currentItem(items, segment);
  const rowRef = useRef<HTMLDivElement>(null);
  const userScrolledAt = useRef(0);

  useEffect(() => {
    const row = rowRef.current;
    if (!row || current < 0 || Date.now() - userScrolledAt.current < USER_SCROLL_PAUSE_MS) return;
    const card = row.querySelector<HTMLElement>(`[data-index="${current}"]`);
    if (!card) return;
    // Measure against the row itself: offsetLeft is relative to the nearest positioned ancestor,
    // which isn't the row, and overshoots the current card out of view.
    const cardLeft =
      card.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft;
    const target = cardLeft - row.clientWidth / 3 + card.offsetWidth / 2;
    const reduce = prefersReducedMotion();
    row.scrollTo({ left: Math.max(0, target), behavior: reduce ? 'auto' : 'smooth' });
  }, [current, items]);

  const seekTo = (segments: number[]) => {
    // In Summary, jump to the next time this chord comes up, else its first.
    const next = segments.find((i) => i > segment) ?? segments[0];
    const seg = next === undefined ? undefined : result.segments[next];
    if (seg) onSeekBeat(seg.startBeat);
  };

  const fretted = instrument === 'piano' ? null : instrument;
  const prefs = useVoicingPrefs();

  return (
    <section aria-label="Chord diagrams" className={styles.section}>
      <div className={styles.header}>
        <span className={styles.title}>Chord diagrams</span>
        <Segmented
          label="Diagram order"
          options={MODES}
          value={mode}
          onChange={onMode}
          tone="surface"
        />
        <div className={styles.instruments}>
          <InstrumentSelect label="Diagram instrument" value={instrument} onChange={onInstrument} />
        </div>
      </div>
      <div
        ref={rowRef}
        className={styles.row}
        data-testid="diagram-strip"
        onWheel={() => {
          userScrolledAt.current = Date.now();
        }}
        onTouchMove={() => {
          userScrolledAt.current = Date.now();
        }}
      >
        {items.map((item, i) => (
          <button
            key={item.key}
            type="button"
            className={styles.card}
            data-index={i}
            data-state={
              i === current
                ? 'current'
                : mode === 'animated' && current >= 0 && i < current
                  ? 'past'
                  : undefined
            }
            aria-label={`${item.label}${i === current ? ', playing now' : ''}`}
            aria-current={i === current || undefined}
            onClick={() => {
              seekTo(item.segments);
            }}
          >
            <span className={styles.mini} data-instrument={instrument} aria-hidden="true">
              <span className={styles.scale}>
                {fretted ? (
                  <FretDiagram
                    layout={layoutFor(item.chord, fretted, options, leftHanded, prefs)}
                    strings={TUNINGS[fretted].length}
                    spacing={STRING_SPACING[fretted]}
                  />
                ) : (
                  <PianoDiagram keys={pianoKeys(item.chord, options.transpose)} />
                )}
              </span>
            </span>
            <span className={styles.label}>{item.label}</span>
          </button>
        ))}
        {items.length === 0 && (
          <span className={styles.empty}>No chords were detected in this song.</span>
        )}
      </div>
    </section>
  );
}
